// Source of truth for documented XYZ instrument tickers:
// https://docs.trade.xyz/consolidated-resources/specification-index
//
// IMPORTANT: both maps below are hand-maintained and are NOT auto-synced with the live
// xyz universe, which has grown well past what's mapped here (90+ markets as of mid-2026).
// A coin missing from XYZ_DOCUMENTED_INSTRUMENTS falls back to its raw symbol for the
// ticker; a coin missing from XYZ_MARKET_NAMES falls back to its raw symbol for the *name*,
// so it can only be found in market search by its symbol — NOT by company name. To make a
// market searchable/displayable by name, add it to XYZ_MARKET_NAMES below (plus a Tolgee
// `market.asset.xyz.<coin>` key for ko/zh localization).
//
// (As of 2026-04-03 these live coins were absent from the spec page and fall back to raw
// symbols — no longer an exhaustive list: URANIUM, ALUMINIUM, DXY, GME, KR200, SOFTBANK,
// JP225, KIOXIA, VIX, LITE, CORN, XLE, WHEAT)

export const XYZ_DOCUMENTED_INSTRUMENTS: Record<string, string> = {
	AAPL: "AAPL",
	AMD: "AMD",
	AMZN: "AMZN",
	BABA: "BABA",
	BRENTOIL: "BRENTOIL",
	CL: "WTIOIL",
	COIN: "COIN",
	COPPER: "COPPER",
	COST: "COST",
	CRCL: "CRCL",
	CRWV: "CRWV",
	DKNG: "DKNG",
	EUR: "EUR",
	EWJ: "EWJ",
	EWY: "EWY",
	GOLD: "GOLD",
	GOOGL: "GOOGL",
	HIMS: "HIMS",
	HOOD: "HOOD",
	HYUNDAI: "HYUNDAI",
	INTC: "INTC",
	JPY: "JPY",
	LLY: "LLY",
	META: "META",
	MSTR: "MSTR",
	MSFT: "MSFT",
	MU: "MU",
	NATGAS: "NATGAS",
	NFLX: "NFLX",
	NVDA: "NVDA",
	ORCL: "ORCL",
	PALLADIUM: "PALLADIUM",
	PLATINUM: "PLATINUM",
	PLTR: "PLTR",
	RIVN: "RIVN",
	SILVER: "SILVER",
	SKHX: "SKHYNIX",
	SMSN: "SAMSUNG",
	SNDK: "SNDK",
	SP500: "SP500",
	TSLA: "TSLA",
	TSM: "TSM",
	URNM: "URNM",
	USAR: "USAR",
	XYZ100: "XYZ100",
};

export const XYZ_MARKET_NAMES: Record<string, string> = {
	AAPL: "Apple",
	AMD: "AMD",
	AMZN: "Amazon",
	BABA: "Alibaba",
	BRENTOIL: "Brent Oil",
	CL: "WTI Crude Oil",
	COIN: "Coinbase",
	COPPER: "Copper",
	COST: "Costco",
	CRCL: "Circle",
	CRWV: "CoreWeave",
	DKNG: "DraftKings",
	EUR: "Euro",
	EWJ: "iShares MSCI Japan ETF",
	EWY: "iShares MSCI South Korea ETF",
	GOLD: "Gold",
	GOOGL: "Alphabet",
	HIMS: "Hims & Hers",
	HOOD: "Robinhood",
	HYUNDAI: "Hyundai Motor",
	INTC: "Intel",
	JPY: "Japanese Yen",
	LLY: "Eli Lilly",
	META: "Meta",
	MSTR: "Strategy",
	MSFT: "Microsoft",
	MU: "Micron",
	NATGAS: "Natural Gas",
	NFLX: "Netflix",
	NVDA: "NVIDIA",
	ORCL: "Oracle",
	PALLADIUM: "Palladium",
	PLATINUM: "Platinum",
	PLTR: "Palantir",
	RIVN: "Rivian",
	SILVER: "Silver",
	SKHX: "SK hynix",
	SMSN: "Samsung Electronics",
	SNDK: "Sandisk",
	SP500: "S&P 500",
	SPCX: "SpaceX",
	TSLA: "Tesla",
	TSM: "Taiwan Semiconductor",
	URNM: "Sprott Uranium Miners ETF",
	USAR: "USA Rare Earth",
	XYZ100: "XYZ100",
};

const warnedUndocumentedXyzCoins = new Set<string>();

function warnUndocumentedXyzCoin(coin: string) {
	if (!import.meta.env.DEV) return;
	if (warnedUndocumentedXyzCoins.has(coin)) return;
	warnedUndocumentedXyzCoins.add(coin);
	console.warn(
		`[xyz-markets] Missing documented instrument mapping for "${coin}". Falling back to the raw coin symbol.`,
	);
}

export function getXyzInstrument(coin: string) {
	const instrument = XYZ_DOCUMENTED_INSTRUMENTS[coin];
	if (!instrument) {
		warnUndocumentedXyzCoin(coin);
		return coin;
	}
	return instrument;
}

export function getXyzMarketName(coin: string) {
	return XYZ_MARKET_NAMES[coin] ?? getXyzInstrument(coin);
}
